/**
 * CreateAppointmentPicker.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Picker de data/hora para criação de novo agendamento.
 * Exibe seletor de profissional + mini calendário semanal + slots disponíveis.
 * Reutiliza a lógica e visual do ReschedulePanel.
 *
 * Ao selecionar um slot e confirmar, chama onSlotSelected({ date, hour, minute, resourceId })
 * para que o pai abra o AppointmentDrawer com o slot e a agenda escolhida preenchidos.
 *
 * @module CreateAppointmentPicker
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  isToday,
  isSameDay,
} from '../../utils/calendarUtils';
import { WEEKDAYS_SHORT, MONTHS } from '../../constants/calendarConstants';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { GetAvailableSlotsAPI } from '../../../../../api/API';
import { getCurrentUser } from '../../../../../utils/RoleManager';
import { getSlotIntervalMinutes } from '../../../../../consts/CalendarConfig';
import {
  SLOT_REASON_OUTSIDE,
  SLOT_REASON_PAST,
  isSlotPast,
  classifyUnavailableSlot,
  professionalWarning,
  isSlotSelectable,
  withApiSlotTimes,
} from '../../utils/slotAvailabilityUtils';
import styles from '../ReschedulePanel/ReschedulePanel.module.css';

// ── Constantes ────────────────────────────────────────────────────────────────

const SLOT_START_HOUR = 7;
const SLOT_END_HOUR = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function getWeekDates(weekOffset) {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek + weekOffset * 7);
  sunday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });
}

function generateDaySlots(intervalMinutes) {
  const slots = [];
  for (let h = SLOT_START_HOUR; h < SLOT_END_HOUR; h++) {
    for (let m = 0; m < 60; m += intervalMinutes) {
      slots.push({ hour: h, minute: m, label: formatTime(h, m) });
    }
  }
  return slots;
}

// Motivos e cores de um horário indisponível: passado (cinza sólido), fora do
// expediente / intervalo (listrado) e ocupado (vermelho). Ver slotAvailabilityUtils.

// ── Ícones SVG ────────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── Componente ────────────────────────────────────────────────────────────────

export default function CreateAppointmentPicker({
  isOpen,
  onClose,
  onSlotSelected,
}) {
  const { CompanyConfig } = DataProviderInstance();

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedResource, setSelectedResource] = useState('');

  // ── Estado dos slots da API ─────────────────────────────────────────────────
  const [apiSlots, setApiSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  // ── Profissionais disponíveis ────────────────────────────────────────────────
  // "Só os meus pacientes": só a própria agenda, como no formulário do agendamento
  const currentUser = getCurrentUser();
  const ownResourceId = currentUser?.isHealthProfessional && currentUser?.scheduleScope === 'own' && currentUser?.resourceId != null
    ? String(currentUser.resourceId)
    : null;
  const resources = useMemo(() => {
    const active = (CompanyConfig?.Resources || []).filter(r => r.Active == null || r.Active == 1);
    const own = ownResourceId ? active.filter(r => String(r.Id) === ownResourceId) : [];
    return own.length > 0 ? own : active;
  }, [CompanyConfig?.Resources, ownResourceId]);

  // ── Auto-seleção quando há apenas um profissional ─────────────────────────
  useEffect(() => {
    if (isOpen && resources.length === 1 && !selectedResource) {
      setSelectedResource(String(resources[0].Id));
    }
  }, [isOpen, resources, selectedResource]);

  // ── Reset ao abrir ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setWeekOffset(0);
      setSelectedDate(null);
      setSelectedSlot(null);
      setSelectedResource(resources.length === 1 ? String(resources[0].Id) : '');
      setApiSlots(null);
      setLoadingSlots(false);
      setSlotsError(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Semana atual ────────────────────────────────────────────────────────────
  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);

  const activeDateObj = useMemo(
    () => selectedDate || weekDates.find(d => isToday(d)) || weekDates[0],
    [selectedDate, weekDates]
  );

  const activeDateKey = activeDateObj
    ? `${activeDateObj.getFullYear()}-${String(activeDateObj.getMonth() + 1).padStart(2, '0')}-${String(activeDateObj.getDate()).padStart(2, '0')}`
    : null;

  // ── Buscar slots da API ao mudar de dia ou profissional ─────────────────────
  useEffect(() => {
    if (!activeDateKey || !selectedResource) {
      setApiSlots(null);
      return;
    }

    setLoadingSlots(true);
    setSlotsError(null);
    setApiSlots(null);

    GetAvailableSlotsAPI(selectedResource, activeDateKey, (response) => {
      setLoadingSlots(false);
      if (response?.error) {
        setSlotsError(response.error);
        setApiSlots(null);
        return;
      }
      if (response?.success && Array.isArray(response.slots)) {
        setApiSlots(response.slots);
      } else {
        setApiSlots(null);
      }
    });
  }, [activeDateKey, selectedResource]);

  // ── Slots do dia ────────────────────────────────────────────────────────────
  const slotIntervalMinutes = getSlotIntervalMinutes(CompanyConfig?.TimeInterval);
  // Grade da clínica + horários do intervalo próprio do profissional (vindos da API)
  const allSlots = useMemo(
    () => withApiSlotTimes(generateDaySlots(slotIntervalMinutes), apiSlots),
    [slotIntervalMinutes, apiSlots]
  );

  const blockedTimes = CompanyConfig?.BlockedTimes;
  const endHour = Array.isArray(CompanyConfig?.AvailableTime) ? Number(CompanyConfig.AvailableTime[1]) : null;

  const classifiedSlots = useMemo(() => {
    if (!activeDateObj) return [];
    return allSlots.map(slot => {
      if (apiSlots !== null) {
        const apiSlot = apiSlots.find(s => s.time === slot.label);
        if (!apiSlot) {
          // Slot não retornado pela API = fora do expediente (ou já passou)
          return isSlotPast(activeDateObj, slot.hour, slot.minute)
            ? { ...slot, status: 'past', reason: SLOT_REASON_PAST }
            : { ...slot, status: 'blocked', reason: SLOT_REASON_OUTSIDE };
        }
        if (apiSlot.available) return { ...slot, status: 'available' };
        // Fora do horário ou no limite do profissional: escolhe, mas com aviso
        const warning = professionalWarning(apiSlot);
        if (warning) return { ...slot, ...warning };
        return {
          ...slot,
          ...classifyUnavailableSlot(slot, {
            date: activeDateObj,
            intervalMinutes: slotIntervalMinutes,
            endHour: Number.isNaN(endHour) ? null : endHour,
            blockedTimes,
          }),
        };
      }
      // Sem API: todos disponíveis (fallback)
      return { ...slot, status: 'available' };
    });
  }, [activeDateObj, allSlots, apiSlots, slotIntervalMinutes, endHour, blockedTimes]);

  const morningSlots = classifiedSlots.filter(s => s.hour < 12);
  const afternoonSlots = classifiedSlots.filter(s => s.hour >= 12 && s.hour < 18);
  const eveningSlots = classifiedSlots.filter(s => s.hour >= 18);
  const hasWarningSlots = classifiedSlots.some(s => s.status === 'warning');

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleDaySelect = useCallback((date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setApiSlots(null);
  }, []);

  const handleSlotSelect = useCallback((slot) => {
    if (!isSlotSelectable(slot)) return;
    setSelectedSlot(slot);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedSlot || !activeDateObj) return;
    onSlotSelected?.({
      date: activeDateObj,
      hour: selectedSlot.hour,
      minute: selectedSlot.minute,
      resourceId: selectedResource,
    });
  }, [selectedSlot, activeDateObj, selectedResource, onSlotSelected]);

  if (!isOpen) return null;

  // Labels
  const activeDayLabel = activeDateObj
    ? `${['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'][activeDateObj.getDay()]}, ${activeDateObj.getDate()} de ${MONTHS[activeDateObj.getMonth()]}`
    : '';

  const selectedSlotLabel = selectedSlot && activeDateObj
    ? `${String(activeDateObj.getDate()).padStart(2,'0')}/${String(activeDateObj.getMonth()+1).padStart(2,'0')} às ${selectedSlot.label}`
    : null;

  // ── Renderiza grid de slots (reutilizado para cada período) ──────────────
  const renderSlotGrid = (slots) => (
    <div className={styles.slotsGrid}>
      {slots.map(slot => {
        const isSelected = selectedSlot?.hour === slot.hour && selectedSlot?.minute === slot.minute;
        const slotClass = isSelected
          ? styles.slotSelected
          : slot.status === 'available'
            ? styles.slotAvailable
            : slot.status === 'warning'
              ? styles.slotWarning
            : slot.status === 'occupied'
              ? styles.slotOccupied
              : slot.status === 'past'
                ? styles.slotPast
                : styles.slotBlocked;

        const isUnavailable = !isSlotSelectable(slot);

        // aria-disabled em vez de disabled: o navegador não mostra o title
        // (motivo) em botão desabilitado. O clique já é barrado no handleSlotSelect.
        return (
          <button
            key={slot.label}
            type="button"
            className={`${styles.slotBtn} ${slotClass}`}
            onClick={() => handleSlotSelect(slot)}
            aria-disabled={isUnavailable}
            title={slot.reason}
            aria-label={`${slot.label} — ${slot.reason || (isUnavailable ? 'indisponível' : 'disponível')}`}
            aria-pressed={isSelected}
          >
            {slot.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={styles.panelContainer} role="dialog" aria-modal="true" aria-label="Criar agendamento" data-tour="agenda-picker">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.drawerHeaderTop}>
        <h2 className={styles.drawerTitle}>Criar agendamento</h2>
        <button
          className={styles.drawerCloseBtn}
          onClick={onClose}
          aria-label="Fechar"
          title="Fechar"
          type="button"
        >
          <XIcon />
        </button>
      </div>
      <div className={styles.drawerHeaderDivider} />

      {/* ── Seletor de profissional ─────────────────────────────────────────── */}
      <div className={styles.panelHeader} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
        <label style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#475569',
          letterSpacing: '0.02em',
        }}>
          Agenda
        </label>
        <select
          value={selectedResource}
          onChange={(e) => {
            setSelectedResource(e.target.value);
            setSelectedSlot(null);
            setApiSlots(null);
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '13px',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            background: '#F8FAFC',
            color: '#1A2332',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="">Selecione uma agenda...</option>
          {resources.map(r => (
            <option key={r.Id} value={String(r.Id)}>{r.Label}</option>
          ))}
        </select>
      </div>

      {/* ── Corpo ───────────────────────────────────────────────────────────── */}
      <div className={styles.panelBody}>

        {!selectedResource ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            color: '#94A3B8',
            fontSize: '13px',
            textAlign: 'center',
          }}>
            Selecione uma agenda para ver os horários disponíveis
          </div>
        ) : (
          <>
            {/* ── Navegação semanal ──────────────────────────────────────── */}
            <div className={styles.weekNav}>
              <button
                type="button"
                className={styles.weekNavBtn}
                onClick={() => setWeekOffset(prev => prev - 1)}
                aria-label="Semana anterior"
              >
                ‹
              </button>

              <div className={styles.weekDays}>
                {weekDates.map((date, i) => {
                  const isSelected = activeDateObj && isSameDay(date, activeDateObj);
                  const isTodayDate = isToday(date);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`${styles.weekDay} ${isSelected ? styles.weekDaySelected : ''} ${isTodayDate && !isSelected ? styles.weekDayToday : ''}`}
                      onClick={() => handleDaySelect(date)}
                      aria-label={`${date.getDate()} de ${MONTHS[date.getMonth()]}`}
                      aria-pressed={isSelected}
                    >
                      <span className={styles.weekDayLabel}>
                        {WEEKDAYS_SHORT[date.getDay()]}
                      </span>
                      <span className={styles.weekDayNumber}>
                        {date.getDate()}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className={styles.weekNavBtn}
                onClick={() => setWeekOffset(prev => prev + 1)}
                aria-label="Próxima semana"
              >
                ›
              </button>
            </div>

            {/* ── Título do dia ──────────────────────────────────────────── */}
            <div className={styles.selectedDayTitle}>{activeDayLabel}</div>

            {/* ── Feedback da API ────────────────────────────────────────── */}
            {loadingSlots && (
              <div className={styles.slotsLoading}>
                <span className={styles.spinner} />
                <span>Verificando disponibilidade...</span>
              </div>
            )}
            {!loadingSlots && slotsError && (
              <div className={styles.slotsError}>
                <WarningAmberOutlinedIcon className={styles.slotsErrorIcon} />
                {slotsError}
              </div>
            )}

            {/* ── Slots ─────────────────────────────────────────────────── */}
            <div className={`${styles.slotsSection}${loadingSlots ? ` ${styles.slotsSectionLoading}` : ''}`} aria-busy={loadingSlots}>
              {morningSlots.length > 0 && (
                <>
                  <div className={styles.slotsPeriodLabel}>Manhã</div>
                  {renderSlotGrid(morningSlots)}
                </>
              )}
              {afternoonSlots.length > 0 && (
                <>
                  <div className={styles.slotsPeriodLabel}>Tarde</div>
                  {renderSlotGrid(afternoonSlots)}
                </>
              )}
              {eveningSlots.length > 0 && (
                <>
                  <div className={styles.slotsPeriodLabel}>Noite</div>
                  {renderSlotGrid(eveningSlots)}
                </>
              )}
              {classifiedSlots.length === 0 && (
                <div className={styles.emptySlots}>
                  Nenhum horário disponível para este dia
                </div>
              )}
            </div>

            {/* ── Legenda ────────────────────────────────────────────────── */}
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendDotAvailable}`} />
                Disponível
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendDotOccupied}`} />
                Ocupado
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendDotPast}`} />
                Já passou
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendDotBlocked}`} />
                Fora do expediente
              </div>
            {hasWarningSlots && (
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.legendDotWarning}`} />
                Fora do horário ou no limite do profissional
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      {selectedSlot && (
        <>
          <div className={styles.panelFooterDivider} />
          <div className={styles.panelFooter}>
            {selectedSlotLabel && (
              <div className={styles.selectedSlotInfo}>
                Horário selecionado:{' '}
                <span className={styles.selectedSlotHighlight}>{selectedSlotLabel}</span>
                {selectedSlot.status === 'warning' && (
                  <span className={styles.slotWarningNote}>{selectedSlot.reason}. Dá para continuar.</span>
                )}
              </div>
            )}
            <button
              type="button"
              className={styles.btnConfirm}
              onClick={handleConfirm}
            >
              Continuar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
